import { Dropdown, InputField, Spinner } from '@sonnen/web-ui';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../../../../AppContext/AppContext';
import { InfoBanner } from '../../../../components/InfoBanner/InfoBanner';
import { AppRoute } from '../../../../constants';
import { useAddressService } from '../../../../hooks/useAddressService';
import { useCurrentRoute } from '../../../../hooks/useCurrentRoute';
import { useValidation } from '../../../../hooks/useValidation';
import { useTranslation } from '../../../../i18n/i18n';
import { useOfferContext } from '../../../../OfferContext/OfferContext';
import { logAddressRefetchError } from '../../../../tools/addressValidationLogging';
import { TAddress } from '../../../../types';
import { removeEmptySpaces, stringToOptionType } from '../../../../utils/utils';
import { commonFieldValidator } from '../../../../utils/validation';
import styles from './AdditionalAddress.module.css';

const I18N_SCOPE = 'data.address';

const AdditionalAddress = ({
  onBlur: onBlurProp,
  expandedInputs = false,
}: {
  onBlur: (field: string) => void;
  expandedInputs?: boolean;
}) => {
  const { t } = useTranslation();
  const currentRoute = useCurrentRoute();
  const homePage = currentRoute === AppRoute.ROOT;
  const { formValues, setFormValues } = useAppContext();
  const {
    offerError,
    productConfigurationsInput: productConfigurationInput,
    setProductConfigurationsInput,
  } = useOfferContext();
  const { address, cities, streets, cityLoading, streetLoading, refetch } = useAddressService(
    formValues.deliveryAddress,
  );
  const [skipStreetBlurValidation, setSkipStreetBlurValidation] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { city, streetName, streetNumber, postalCode } = address;
  const multipleCities = cities.length > 1;

  const validationSchema = useMemo(
    () => ({
      city: {
        required: multipleCities,
        message: t(`${I18N_SCOPE}.cityError`),
      },
      streetName: {
        required: true,
        message: t(`${I18N_SCOPE}.streetNameError`),
      },
      streetNumber: {
        required: true,
        message: t(`${I18N_SCOPE}.streetNumberError`),
        validator: commonFieldValidator,
      },
    }),
    [t],
  );

  const [validate, { errors, setErrors }] = useValidation<TAddress>({
    values: address,
    schema: validationSchema,
    lazy: true,
  });

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (offerError) {
      refetch?.({ postalCode, city: '' })?.catch((error) => {
        logAddressRefetchError(error, offerError);
      });
    }
  }, [offerError]);

  const onChange = (field: string, value: string) => {
    const deliveryAddress = { ...address, [field]: removeEmptySpaces(value) };
    setFormValues({ ...formValues, deliveryAddress });
  };

  const onCityChange = (_: string, value: string) => {
    if (value !== city) {
      const deliveryAddress = {
        ...address,
        city: value,
        streetName: '',
        streetNumber: '',
      };

      setFormValues({ ...formValues, deliveryAddress });
      setProductConfigurationsInput({ ...productConfigurationInput, deliveryAddress });
      setErrors({ ...errors, streetName: '', streetNumber: '' });
    }
  };

  const onStreetChange = (field: string, value: string) => {
    // Mark that onChange just fired so the immediately-following onBlur (on mobile)
    // skips revalidation — address state in useAddressService is still stale at that point.
    setSkipStreetBlurValidation(true);
    onChange(field, value);
    setErrors({ ...errors, streetName: '' });
  };

  const onBlur = (field: string) => {
    if (field === 'streetName' && skipStreetBlurValidation) {
      setSkipStreetBlurValidation(false);
      onBlurProp?.(field);
      return;
    }
    validate([field]);
    onBlurProp?.(field);
  };

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={t('offer.additionalAddressTitle')}
      className={clsx(styles.additionalAddress, { [styles.columnLayout]: homePage })}
      data-testid={`additional-address-section${offerError ? `-${offerError}` : ''}`}
      tabIndex={-1}
    >
      <InfoBanner variant="secondary" iconSize="sm" typography="Label2">
        {t('offer.additionalAddressTitle')}
      </InfoBanner>
      {cityLoading ? (
        <div className={styles.spinner}>
          <Spinner size="md" accessibilityLabel={t('loadingCities')} />
        </div>
      ) : (
        <div
          className={clsx(styles.address, {
            [styles.multipleCities]: multipleCities,
            [styles.expandedInputs]: expandedInputs,
          })}
        >
          {multipleCities && (
            <div data-testid="index-order" className={styles.cityIndex}>
              <Dropdown
                name="city"
                dataTestId="city-select"
                value={stringToOptionType(city || '')}
                options={cities.map(stringToOptionType)}
                label={t(`${I18N_SCOPE}.city`)}
                searchable={false}
                clearable={false}
                disabled={!cities.length}
                error={errors.city}
                onChange={onCityChange}
                onBlur={onBlur}
              />
            </div>
          )}
          {!multipleCities && city && (
            <InputField
              name="city"
              dataTestId="city"
              label={t(`${I18N_SCOPE}.city`)}
              value={city}
              onChange={onChange}
              onBlur={onBlur}
              expanded
              disabled
            />
          )}

          <Dropdown
            name="streetName"
            dataTestId="street-select"
            value={stringToOptionType(streetName || '')}
            options={streets.map(stringToOptionType)}
            label={t(`${I18N_SCOPE}.streetName`)}
            searchable
            clearable={false}
            loading={streetLoading}
            disabled={!streets.length || streetLoading}
            error={errors.streetName}
            onChange={onStreetChange}
            onBlur={onBlur}
          />

          <InputField
            name="streetNumber"
            dataTestId="street-number"
            label={t(`${I18N_SCOPE}.streetNumber`)}
            value={streetNumber || ''}
            onChange={onChange}
            onBlur={onBlur}
            error={errors.streetNumber}
            className={styles.number}
            maxLength={10}
            expanded
          />
        </div>
      )}
    </div>
  );
};

export default AdditionalAddress;
